import React, { FC, useContext, useEffect, useMemo, useRef } from 'react';
import styles from './index.module.scss';
import ReactEcharts from 'echarts-for-react';
import { EnergyFlowChartResponse } from '@/api/plustekSankey';
import { Spin } from 'antd';
import { Empty } from '@maxtropy/components';
import { formatNum, getNodeMaxDepth, getSankeyHeight } from '@/pages/PlustekSankey/components/UnitSankey';
import { useRequest } from 'ahooks';
import { apiV2EnergyAnalysisGetDifferenceDescPost } from '@maxtropy/device-customer-apis-v2';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
import { keepPercentOrLine } from '@/utils/utils';

const color = ['#57FB8BCC', '#52E7FFCC', '#2D8DFFCC', '#CE90D1CC', '#FF9247CC'];

interface IFlowEcharts {
  data?: EnergyFlowChartResponse;
  physicalUnitName?: string;
  isMedium: boolean;
  flowChartLoading: boolean;
}

const FlowEcharts: FC<IFlowEcharts> = props => {
  const { data, physicalUnitName, isMedium, flowChartLoading } = props;
  const { scale } = useContext(ShareDataWrapperContext);

  const { data: differenceDesc } = useRequest(apiV2EnergyAnalysisGetDifferenceDescPost);

  const chartHeight = useMemo(() => {
    const height = getSankeyHeight(data?.nodes ?? [], scale);
    return height;
  }, [data]);

  const chartOption = useMemo(() => {
    return {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        backgroundColor: '#000',
        borderColor: 'transparent',
        textStyle: {
          color: 'rgba(255,255,255,0.65)',
          fontSize: 12,
        },
        formatter: (v: any) => {
          let res = '';
          if (v.dataType === 'node') {
            res = `<div>
          <p>${v.data.energyUnitName}</p>
          <p style="color: rgba(255,255,255,0.85); margin-bottom: 0px;" >${formatNum(
            v.value,
            isMedium,
            physicalUnitName
          )}</p>
           ${
             v?.data?.depth !== 0
               ? ` <p style="color: rgba(255,255,255,0.85);margin-top:8px; margin-bottom: 0px;">
                  占比：${keepPercentOrLine(v.data?.proportion)}%
                  </p>`
               : ''
           }
          </div>`;
          }
          if (v.dataType === 'edge') {
            const sourceName = data?.nodes?.find(i => i.key === v.data.source)?.energyUnitName;
            const targetName = data?.nodes?.find(i => i.key === v.data.target)?.energyUnitName;
            res = `<div>
          <p>${sourceName}——${targetName}</p>
          <p style="color: rgba(255,255,255,0.85); margin-bottom: 0px;" >${formatNum(
            v.value,
            isMedium,
            physicalUnitName
          )}</p>
           <p style="color: rgba(255,255,255,0.85);margin-top:8px;margin-bottom: 0px;" >下级占比：${keepPercentOrLine(
             v.data?.proportion
           )}%</p>
          </div>`;
          }

          return res;
        },
      },
      series: [
        {
          type: 'sankey',
          left: 10,
          right: 10,
          top: 40,
          bottom: 10,
          nodeGap: 8,
          layoutIterations: 0, // 解决能流图衔接问题
          nodeAlign: 'justify',
          label: {
            color: 'rgba(255,255,255,0.85)',
            rich: {
              a: {
                color: 'rgb(179,179,179)',
              },
              b: {
                color: '#ABD335',
              },
            },
            formatter: (v: any) => {
              if (v.data.energyUnitName === (differenceDesc?.downstreamGreaterThanUpstream || '未知能耗')) {
                return `{a|${v.data.energyUnitName}}`;
              }
              if (v.data.energyUnitName === (differenceDesc?.upstreamGreaterThanDownstream || '用能损耗')) {
                return `{b|${v.data.energyUnitName}}`;
              }
              return `${v.data.energyUnitName}`;
            },
          },
          labelLayout: {
            hideOverlap: true,
          },
          draggable: false,
          data: data?.nodes?.map(i => {
            return {
              ...i,
              itemStyle: {
                color:
                  i.energyUnitName === (differenceDesc?.upstreamGreaterThanDownstream || '用能损耗')
                    ? '#ABD335'
                    : i.energyUnitName === (differenceDesc?.downstreamGreaterThanUpstream || '未知能耗')
                    ? 'rgb(179,179,179)'
                    : color[i.depth % color.length],
              },
              label: {
                position: i.depth === getNodeMaxDepth(data?.nodes) ? 'left' : 'right',
              },
            };
          }),
          links: data?.links?.map(i => {
            return {
              ...i,
              lineStyle: {
                color: 'gradient',
                opacity: 0.3,
                // curveness: 0.5, // sankey图边曲度，尽量不要设置，在苹果电脑上展示会有问题
              },
            };
          }),
        },
      ],
    };
  }, [data, physicalUnitName, isMedium]);

  const chartRef = useRef<ReactEcharts>();
  const handleResize = () => {
    const echartsInstance = chartRef.current?.getEchartsInstance();
    if (echartsInstance) {
      echartsInstance.resize();
    }
  };

  const containerRef = useRef(null);
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const echartsInstance = chartRef.current?.getEchartsInstance();
      if (echartsInstance) {
        echartsInstance.resize();
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isEmptyChart = useMemo(() => {
    const all_depth = (data?.nodes ?? []).map(i => i.depth);
    const max_depth = Math.max(...all_depth);
    const min_depth = Math.min(...all_depth);
    return data?.nodes.length === 0 || data?.links.length === 0 || max_depth === min_depth;
  }, [data]);

  return (
    <div
      className={styles.charts_wrapper}
      ref={containerRef}
      style={{
        minHeight: 560,
        height: chartHeight,
      }}
    >
      {flowChartLoading ? (
        <Spin spinning={flowChartLoading} />
      ) : isEmptyChart ? (
        <Empty />
      ) : (
        <ReactEcharts style={{ height: '100%', width: '100%' }} option={chartOption} notMerge lazyUpdate={false} />
      )}
    </div>
  );
};

export default FlowEcharts;
